import fetcher from '@/core/lib/fetcher'
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {
  CurrencyConversion,
  Organization,
  OrganizationFormValues,
} from './organization.types'
import { APIError, APIResponse } from '@/core/types/utils'
import { toast } from '../ui/use-toast'

export const organizationQueryOptions = (
  token: string,
  startDate?: string,
  endDate?: string
) =>
  queryOptions({
    queryKey: ['organizations', token, startDate, endDate],
    queryFn: async () => {
      return await fetcher
        .auth(`Bearer ${token}`)
        .get(
          import.meta.env.VITE_API_BOOKING +
            `admin/organizations${startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ''}`
        )
        .json<APIResponse<Organization[]>>()
    },
  })

export const createOrganization = (
  organization: OrganizationFormValues,
  token: string
) => {
  return fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + 'admin/organizations')
    .post(organization)
    .json<APIResponse<Organization>>()
}

export const updateOrganization = (
  organization: OrganizationFormValues,
  organizationId: string,
  token: string
) => {
  return fetcher
    .auth(`Bearer ${token}`)
    .url(
      import.meta.env.VITE_API_BOOKING + `admin/organizations/${organizationId}`
    )
    .put(organization)
    .json<APIResponse<Organization>>()
}

export const organizationByIdQueryOptions = (id: string, token: string) =>
  queryOptions({
    queryKey: ['organization', id, token],
    queryFn: () => {
      return fetcher
        .auth(`Bearer ${token}`)
        .get(import.meta.env.VITE_API_BOOKING + `admin/organizations/${id}`)
        .json<APIResponse<Organization>>()
    },
  })

export const useDeleteOrganization = (token: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(import.meta.env.VITE_API_BOOKING + `admin/organizations/${id}`)
        .delete()
        .json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] })
    },
    onError: (error) => {
      const errorObject: APIError<null> = JSON.parse(error.message)
      const errorMessage = errorObject.message

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })
}

export const getCurrencyConversionQueryOptions = () =>
  queryOptions({
    queryKey: ['currencyConversion'],
    queryFn: async () => {
      return await fetcher
        .get(
          'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json'
        )
        .json<CurrencyConversion>()
    },
    staleTime: 1000 * 60 * 60 * 8, // 8 hours
  })
